import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {Store} from "@ngrx/store";
import {take} from "rxjs";
import {ASSET_MANAGEMENT_URL} from "../../../app.config";
import {API_ENDPOINTS, APP_CONSTANTS} from "../../../app.constants";
import {ToastSeverityEnum} from "../../../enums";
import {prepareFiltersForParams} from "../../../helpers";
import {DtoTechnologyResponseResultsInterface, TechnologyModel} from "../../../interfaces";
import {mapTechnologyFromDtoArray, mapTechnologyToDto} from "../../../mapping";
import {
  addTechnologies,
  addTechnology,
  setPaginatedTechnologies,
  setTechnologies,
  setTechnologiesPaginationInfo,
  updateTechnology
} from "../../../store/actions/technologies.actions";
import {selectTechnologyFilters} from "../../../store/reducers/technologies.reducer";
import {ToastsService} from "../../app-services";

@Injectable({
  providedIn: "root"
})
export class TechnologiesService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly toastsService = inject(ToastsService);
  private readonly assetManagementUrl = inject(ASSET_MANAGEMENT_URL);

  constructor() {
    this.store.select(selectTechnologyFilters).subscribe(() => {
      this.getPaginatedTechnologies();
    });
  }

  public getPaginatedTechnologies() {
    this.store
      .select(selectTechnologyFilters)
      .pipe(take(1))
      .subscribe(filters => {
        const queryParams = prepareFiltersForParams(filters);
        this.httpClient
          .get<DtoTechnologyResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.TECHNOLOGIES, {params: queryParams})
          .subscribe({
            next: data => {
              this.store.dispatch(setPaginatedTechnologies({technologies: mapTechnologyFromDtoArray(data.results ?? [])}));
              this.store.dispatch(
                setTechnologiesPaginationInfo({
                  paginationInfo: {
                    from: (data.offset ?? 0) + 1,
                    to: (data.offset ?? 0) + (data.count ?? 0),
                    totalResults: data.totalCount ?? 0,
                    currentPage: (data.offset ?? 0) / APP_CONSTANTS.PAGINATION_PAGE_SIZE + 1,
                    totalPages: Math.ceil((data.totalCount ?? 0) / APP_CONSTANTS.PAGINATION_PAGE_SIZE)
                  }
                })
              );
            },
            error: (error: HttpErrorResponse) => {
              this.toastsService.addToast({
                severity: ToastSeverityEnum.Error,
                message: "Error fetching Technologies Paginated, Status " + error.status
              });
            }
          });
      });
  }

  public getAllTechnologies() {
    this.store.dispatch(setTechnologies({technologies: []}));
    this.getTechnologiesRecursive(0);
  }

  private getTechnologiesRecursive(offset: number) {
    this.httpClient
      .get<DtoTechnologyResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.TECHNOLOGIES, {params: {offset: offset}})
      .subscribe({
        next: data => {
          this.store.dispatch(addTechnologies({technologies: mapTechnologyFromDtoArray(data.results ?? [])}));
          if (data.next) {
            this.getTechnologiesRecursive(offset + APP_CONSTANTS.MAX_API_RESULTS_LIMIT);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: "Error fetching Technologies, Status " + error.status
          });
        }
      });
  }

  public createTechnology(model: TechnologyModel): void {
    const idEncoded = encodeURIComponent(model.id ?? "");
    this.httpClient
      .put(this.assetManagementUrl + API_ENDPOINTS.TECHNOLOGIES + `/${idEncoded}`, mapTechnologyToDto(model))
      .subscribe({
        complete: () => {
          this.store.dispatch(addTechnology({technology: model}));
          this.getPaginatedTechnologies();
          this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: `Created new Technology ${model.id}`});
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to create new Technology ${model.id}, Status ${error.status}`
          });
        }
      });
  }

  public updateTechnology(model: TechnologyModel): void {
    const idEncoded = encodeURIComponent(model.id ?? "");
    this.httpClient
      .post(this.assetManagementUrl + API_ENDPOINTS.TECHNOLOGIES + `/${idEncoded}`, mapTechnologyToDto(model))
      .subscribe({
        complete: () => {
          this.store.dispatch(updateTechnology({technology: model}));
          this.getPaginatedTechnologies();
          this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: `Updated Technology ${model.id}`});
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to update Technology ${model.id}, Status ${error.status}`
          });
        }
      });
  }

  public deleteTechnology(technologyId: string): void {
    const technologyIdEncoded = encodeURIComponent(technologyId);
    this.httpClient.delete(this.assetManagementUrl + API_ENDPOINTS.TECHNOLOGIES + `/${technologyIdEncoded}`).subscribe({
      next: () => {
        this.getPaginatedTechnologies();
        this.toastsService.addToast({severity: ToastSeverityEnum.Success, message: `Technology ${technologyId} deleted`});
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to delete Technology ${technologyId}, Status ${error.status}`
        });
      }
    });
  }
}
